import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Loader2,
  FileText,
  Download,
  Trash2,
  Eye,
  Plus,
  Users,
  RefreshCw,
  MapPin,
  Calendar,
  Building2,
  Flag,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaxDocuments = () => {
  const { isOfficeManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [supportedDocs, setSupportedDocs] = useState({});
  
  // Generate dialog state
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    employee_id: "",
    tax_year: new Date().getFullYear(),
    document_type: "",
    country: "Canada",
  });
  
  // Batch generate dialog
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchForm, setBatchForm] = useState({
    document_type: "t4",
    tax_year: new Date().getFullYear(),
    country: "Canada",
  });
  
  // Preview dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  
  // Filter state
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterYear, setFilterYear] = useState("all");

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tax-documents/`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employees?current_only=true`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    }
  }, []);

  const fetchSupportedDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tax-documents/supported-documents`);
      setSupportedDocs(response.data);
    } catch (error) {
      console.error("Failed to fetch supported documents:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDocuments(),
        fetchEmployees(),
        fetchSupportedDocuments(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDocuments, fetchEmployees, fetchSupportedDocuments]);

  const handleGenerateDocument = async () => {
    if (!generateForm.employee_id || !generateForm.document_type) {
      toast.error("Please fill all required fields");
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/tax-documents/generate`, generateForm);
      toast.success(`${response.data.document_type} generated successfully`);
      setGenerateDialogOpen(false);
      setGenerateForm({
        employee_id: "",
        tax_year: new Date().getFullYear(),
        document_type: "",
        country: "Canada",
      });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate document");
    } finally {
      setGenerating(false);
    }
  };

  const handleBatchGenerate = async () => {
    setBatchGenerating(true);
    try {
      const response = await axios.post(
        `${API}/tax-documents/batch-generate?document_type=${batchForm.document_type}&tax_year=${batchForm.tax_year}&country=${batchForm.country}`
      );
      toast.success(`Generated ${response.data.generated_count} documents`);
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} documents failed to generate`);
      }
      setBatchDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Batch generation failed");
    } finally {
      setBatchGenerating(false);
    }
  };

  const handlePreview = async (documentId) => {
    try {
      const response = await axios.get(`${API}/tax-documents/${documentId}`);
      setPreviewDocument(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load document");
    }
  };

  const handleDownload = (documentId) => {
    window.open(`${API}/tax-documents/${documentId}/pdf`, "_blank");
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/tax-documents/${documentId}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const getDocumentTypeBadge = (type, country) => {
    const colors = {
      T4: "bg-red-100 text-red-800",
      ROE: "bg-orange-100 text-orange-800",
      W2: "bg-blue-100 text-blue-800",
      P60: "bg-violet-100 text-violet-800",
      P45: "bg-purple-100 text-purple-800",
    };
    return (
      <Badge className={colors[type] || "bg-gray-100 text-gray-800"}>
        {type}
      </Badge>
    );
  };

  const getCountryFlag = (country) => {
    const flags = {
      Canada: "🇨🇦",
      "United States": "🇺🇸",
      "United Kingdom": "🇬🇧",
    };
    return flags[country] || "🌍";
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesCountry = filterCountry === "all" || doc.country === filterCountry;
    const matchesYear = filterYear === "all" || doc.tax_year.toString() === filterYear;
    return matchesCountry && matchesYear;
  });

  const getAvailableDocTypes = () => {
    const country = generateForm.country;
    return supportedDocs[country]?.documents || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tax-documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tax Documents</h1>
          <p className="text-muted-foreground">
            Generate and manage tax documents for employees across Canada, US, and UK
          </p>
        </div>
        {isOfficeManager && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBatchDialogOpen(true)} data-testid="batch-generate-btn">
              <Users className="h-4 w-4 mr-2" />
              Batch Generate
            </Button>
            <Button onClick={() => setGenerateDialogOpen(true)} data-testid="generate-btn">
              <Plus className="h-4 w-4 mr-2" />
              Generate Document
            </Button>
          </div>
        )}
      </div>

      {/* Country Tabs */}
      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" onClick={() => setFilterCountry("all")}>
            All Countries
          </TabsTrigger>
          <TabsTrigger value="canada" onClick={() => setFilterCountry("Canada")} data-testid="tab-canada">
            🇨🇦 Canada
          </TabsTrigger>
          <TabsTrigger value="us" onClick={() => setFilterCountry("United States")} data-testid="tab-us">
            🇺🇸 United States
          </TabsTrigger>
          <TabsTrigger value="uk" onClick={() => setFilterCountry("United Kingdom")} data-testid="tab-uk">
            🇬🇧 United Kingdom
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {/* Document Types Info */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  🇨🇦 Canada
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-800">T4</Badge>
                    <span>Statement of Remuneration Paid</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-orange-100 text-orange-800">ROE</Badge>
                    <span>Record of Employment</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  🇺🇸 United States
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-800">W-2</Badge>
                    <span>Wage and Tax Statement</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  🇬🇧 United Kingdom
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-violet-100 text-violet-800">P60</Badge>
                    <span>End of Year Certificate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-800">P45</Badge>
                    <span>Details of Employee Leaving</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Documents Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Generated Documents</CardTitle>
                <div className="flex gap-2">
                  <Select value={filterYear} onValueChange={setFilterYear}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Years</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2024">2024</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={fetchDocuments}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Tax Year</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No tax documents generated yet. Click "Generate Document" to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>{getDocumentTypeBadge(doc.document_type, doc.country)}</TableCell>
                        <TableCell className="font-medium">{doc.employee_name}</TableCell>
                        <TableCell>
                          <span className="flex items-center gap-2">
                            {getCountryFlag(doc.country)}
                            {doc.country}
                          </span>
                        </TableCell>
                        <TableCell>{doc.tax_year}</TableCell>
                        <TableCell>{new Date(doc.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {doc.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handlePreview(doc.id)} data-testid={`preview-${doc.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDownload(doc.id)}>
                              <Download className="h-4 w-4" />
                            </Button>
                            {isOfficeManager && (
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(doc.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate Document Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Tax Document</DialogTitle>
            <DialogDescription>
              Create a tax document for an employee
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Country</Label>
              <Select
                value={generateForm.country}
                onValueChange={(v) => setGenerateForm({ ...generateForm, country: v, document_type: "" })}
              >
                <SelectTrigger data-testid="select-country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                  <SelectItem value="United States">🇺🇸 United States</SelectItem>
                  <SelectItem value="United Kingdom">🇬🇧 United Kingdom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select
                value={generateForm.document_type}
                onValueChange={(v) => setGenerateForm({ ...generateForm, document_type: v })}
              >
                <SelectTrigger data-testid="select-doc-type">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableDocTypes().map((doc) => (
                    <SelectItem key={doc.type} value={doc.type}>
                      {doc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Employee</Label>
              <Select
                value={generateForm.employee_id}
                onValueChange={(v) => setGenerateForm({ ...generateForm, employee_id: v })}
              >
                <SelectTrigger data-testid="select-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tax Year</Label>
              <Select
                value={generateForm.tax_year.toString()}
                onValueChange={(v) => setGenerateForm({ ...generateForm, tax_year: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateDocument} disabled={generating} data-testid="confirm-generate-btn">
              {generating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Generate Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Batch Generate Documents</DialogTitle>
            <DialogDescription>
              Generate tax documents for all active employees at once
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Country</Label>
              <Select
                value={batchForm.country}
                onValueChange={(v) => {
                  const defaultType = v === "Canada" ? "t4" : v === "United States" ? "w2" : "p60";
                  setBatchForm({ ...batchForm, country: v, document_type: defaultType });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                  <SelectItem value="United States">🇺🇸 United States</SelectItem>
                  <SelectItem value="United Kingdom">🇬🇧 United Kingdom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select
                value={batchForm.document_type}
                onValueChange={(v) => setBatchForm({ ...batchForm, document_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {batchForm.country === "Canada" && (
                    <SelectItem value="t4">T4 - Statement of Remuneration</SelectItem>
                  )}
                  {batchForm.country === "United States" && (
                    <SelectItem value="w2">W-2 - Wage and Tax Statement</SelectItem>
                  )}
                  {batchForm.country === "United Kingdom" && (
                    <SelectItem value="p60">P60 - End of Year Certificate</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tax Year</Label>
              <Select
                value={batchForm.tax_year.toString()}
                onValueChange={(v) => setBatchForm({ ...batchForm, tax_year: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                This will generate {batchForm.document_type.toUpperCase()} documents for all{" "}
                <span className="font-medium">{employees.length}</span> active employees.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBatchGenerate} disabled={batchGenerating} data-testid="confirm-batch-btn">
              {batchGenerating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Generate All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {previewDocument?.data?.form_type} - {previewDocument?.data?.form_name}
            </DialogTitle>
            <DialogDescription>
              Tax Year: {previewDocument?.tax_year} | {previewDocument?.country}
            </DialogDescription>
          </DialogHeader>
          {previewDocument && (
            <div className="space-y-6">
              {/* Employer Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Employer Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <p className="font-medium">{previewDocument.data.employer?.name}</p>
                  <p className="text-muted-foreground">{previewDocument.data.employer?.address}</p>
                  {previewDocument.data.employer?.business_number && (
                    <p className="text-muted-foreground">BN: {previewDocument.data.employer.business_number}</p>
                  )}
                  {previewDocument.data.employer?.ein && (
                    <p className="text-muted-foreground">EIN: {previewDocument.data.employer.ein}</p>
                  )}
                </CardContent>
              </Card>

              {/* Employee Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Employee Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <p className="font-medium">{previewDocument.data.employee?.name}</p>
                  <p className="text-muted-foreground">{previewDocument.data.employee?.address}</p>
                </CardContent>
              </Card>

              {/* Boxes/Fields */}
              {previewDocument.data.boxes && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Tax Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(previewDocument.data.boxes).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                          <span className="text-sm">
                            <span className="font-medium">Box {key}:</span> {value.label}
                          </span>
                          <span className="font-mono text-sm">
                            ${typeof value.amount === "number" ? value.amount.toLocaleString() : value.amount}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pay and Tax (UK) */}
              {previewDocument.data.pay_and_tax && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Pay and Tax</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-2 bg-muted/50 rounded">
                        <p className="text-xs text-muted-foreground">Pay in this employment</p>
                        <p className="font-mono">£{previewDocument.data.pay_and_tax.pay_in_this_employment?.toLocaleString()}</p>
                      </div>
                      <div className="p-2 bg-muted/50 rounded">
                        <p className="text-xs text-muted-foreground">Tax deducted</p>
                        <p className="font-mono">£{previewDocument.data.pay_and_tax.tax_deducted?.toLocaleString()}</p>
                      </div>
                      <div className="p-2 bg-muted/50 rounded">
                        <p className="text-xs text-muted-foreground">Tax code</p>
                        <p className="font-mono">{previewDocument.data.pay_and_tax.tax_code}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => handleDownload(previewDocument?.id)}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxDocuments;
